import React, { useCallback, useMemo, useState } from 'react'
import Box from '@src/components/Box'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { PricingAndMaterialDetails } from '@src/aem-content/aem-types'
import { FormattedMessage } from 'react-intl'
import DiscoPromoProduct from './DiscoPromoProduct'
import {
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import clsx from 'clsx'
import { ExpandLess } from '@material-ui/icons'
import { Breakpoints } from '@src/utils/useResponsiveSizes'
import DiscoPromoMobileCard from './DiscoPromoMobileCard'
import DiscoPromoProductsSort, {
  Sort,
  SortOption,
} from './DiscoPromoProductsSort'

type DiscoPromoProductTableProps = {
  discoPromoProducts: PricingAndMaterialDetails[]
  loadMoreItems: () => void
  pricingDataLoading?: boolean
  showLoadMoreBtn?: boolean
}

type SortColumn = 'product' | 'productName' | 'productDescription' | null
type SortOrder = 'asc' | 'desc'

export interface SortState {
  column: SortColumn
  order: SortOrder
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(6, 0, 16, 0),
    },
  },
  desktopContainer: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },

  mobileContainer: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },

  productListHeadings: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(2.5),
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightBold,
      '& > div': {
        padding: theme.spacing(0, 2),
        lineHeight: 1.5,
      },
    },
  },

  productNumberText: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      width: '12.5%',
      minWidth: 120,
    },
  },
  productNameText: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      width: '21%',
      minWidth: 180,
    },
  },
  descriptionText: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      width: '45.5%',
      whiteSpace: 'nowrap',
    },
  },
  productContainer: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'column',
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
  },

  loadMoreBtn: {
    alignSelf: 'center',
    marginTop: theme.spacing(6),
    padding: theme.spacing(3, 25.75),
    maxWidth: '288px',
    width: '288px',
    minWidth: '144px',
    lineHeight: '24px',
    height: '40px',
    [theme.breakpoints.up('md')]: {
      maxWidth: '237px',
      minWidth: '144px',
      width: '100%',
      padding: theme.spacing(2, 6),
      marginTop: theme.spacing(4),
    },
    [theme.breakpoints.down(374)]: {
      width: '237px',
      padding: theme.spacing(3, 6),
    },
    [theme.breakpoints.down(343)]: {
      maxWidth: '237px',
      width: '100%',
      padding: theme.spacing(3, 6),
    },
  },
  sortArrowIconUp: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
    transition: 'transform 0.25s',
  },
  sortArrowIconDown: {
    transform: 'rotate(180deg)',
  },
}))

const DiscoPromoProductTable = ({
  discoPromoProducts,
  loadMoreItems,
  pricingDataLoading,
  showLoadMoreBtn,
}: DiscoPromoProductTableProps) => {
  const classes = useStyles()
  const [sortState, setSortState] = useState<SortState>({
    column: Sort.product,
    order: 'desc',
  })

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(Breakpoints.MD), {
    defaultMatches: true,
  })

  /** Sort products */
  const sortedProducts = useMemo(() => {
    if (!sortState.column) return discoPromoProducts
    const { column, order } = sortState
    return [...discoPromoProducts].sort((a, b) => {
      const valA = String(a[column] ?? '').toLowerCase()
      const valB = String(b[column] ?? '').toLowerCase()
      if (valA < valB) return order === 'asc' ? -1 : 1
      if (valA > valB) return order === 'asc' ? 1 : -1
      return 0
    })
  }, [discoPromoProducts, sortState])

  /** Group by product */
  const groupedProducts = useMemo(() => {
    const productGrouped = new Map<string, PricingAndMaterialDetails>()
    const unavailablePricingAvailabilityProducts: PricingAndMaterialDetails[] =
      []

    for (const pricingModel of sortedProducts) {
      const key = pricingModel.product?.toLowerCase() ?? ''
      if (!key) continue
      if (!pricingModel.items?.length) {
        unavailablePricingAvailabilityProducts.push(pricingModel)
        continue
      }
      if (productGrouped.has(key)) {
        const existing = productGrouped.get(key)!
        productGrouped.set(key, {
          ...existing,
          items: [...existing.items, ...pricingModel.items],
        })
      } else
        productGrouped.set(key, {
          ...pricingModel,
          items: [...pricingModel.items],
        })
    }
    return [
      ...Array.from(productGrouped.values()),
      ...unavailablePricingAvailabilityProducts,
    ]
  }, [sortedProducts])

  /** Handle sort */
  const handleSort = useCallback((column: SortColumn) => {
    setSortState((prev) => ({
      column,
      order: prev.column === column && prev.order === 'asc' ? 'desc' : 'asc',
    }))
  }, [])

  const handleMobileSort = useCallback((option: SortOption) => {
    setSortState({ column: option.column, order: option.order })
  }, [])

  return (
    <Box className={classes.container}>
      {isDesktop ? (
        <Box className={classes.desktopContainer}>
          <Box className={classes.productListHeadings}>
            <Box
              className={classes.productNumberText}
              onClick={() => handleSort('product')}
            >
              <FormattedMessage
                id="PRODUCT_NUMBER"
                defaultMessage="Product Number"
              />
              <ExpandLess
                className={clsx(classes.sortArrowIconUp, {
                  [classes.sortArrowIconDown]: !(
                    sortState.column === 'product' && sortState.order === 'asc'
                  ),
                })}
              />
            </Box>

            <Box
              className={classes.productNameText}
              onClick={() => handleSort('productName')}
            >
              <FormattedMessage
                id="PRODUCT_NAME"
                defaultMessage="Product Name"
              />
              <ExpandLess
                className={clsx(classes.sortArrowIconUp, {
                  [classes.sortArrowIconDown]: !(
                    sortState.column === 'productName' &&
                    sortState.order === 'asc'
                  ),
                })}
              />
            </Box>

            <Box
              className={classes.descriptionText}
              onClick={() => handleSort('productDescription')}
            >
              <FormattedMessage id="DESCRIPTION" defaultMessage="Description" />
              <ExpandLess
                className={clsx(classes.sortArrowIconUp, {
                  [classes.sortArrowIconDown]: !(
                    sortState.column === 'productDescription' &&
                    sortState.order === 'asc'
                  ),
                })}
              />
            </Box>
          </Box>

          <Box className={classes.productContainer}>
            {groupedProducts?.map((promoProduct, index) => (
              <DiscoPromoProduct
                key={`${promoProduct.product}-${index}`}
                product={promoProduct?.product}
                productsku={promoProduct?.productsku}
                productName={promoProduct?.productName}
                materialPricing={promoProduct?.items}
                dchainMessage={promoProduct?.dchainMessage}
                productDescription={promoProduct?.productDescription}
                brand={promoProduct?.brand}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Box className={classes.mobileContainer}>
          <DiscoPromoProductsSort
            loading={pricingDataLoading}
            value={sortState}
            handleUpdateSort={(sort) => {
              if (sort) handleMobileSort(sort)
            }}
          />
          <DiscoPromoMobileCard discoPromoProductsSorted={sortedProducts} />
        </Box>
      )}

      {showLoadMoreBtn && (
        <Button
          variant="outlined"
          color="primary"
          onClick={loadMoreItems}
          className={classes.loadMoreBtn}
          disabled={pricingDataLoading}
        >
          {pricingDataLoading ? (
            <CircularProgress size={30} />
          ) : (
            <FormattedMessage id="LOAD_MORE" defaultMessage="Load More" />
          )}
        </Button>
      )}
    </Box>
  )
}

export default DiscoPromoProductTable
